import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { furnitureService, FurnitureData } from '../../services/furniture';
import { useTranslation } from 'react-i18next';

const BuyerDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [recentItems, setRecentItems] = useState<FurnitureData[]>([]);
  const [favoriteCategories] = useState(['sofa', 'desk', 'chair']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentItems = async () => {
      try {
        const response = await furnitureService.getFurniture({ limit: 6 });
        setRecentItems(response.furniture);
      } catch (error) {
        console.error('Error fetching recent items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentItems();
  }, []);

  return (
    <div className="w-full min-w-0 min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('buyer.dashboard.title')}</h1>
          <p className="text-gray-600 mt-2">{t('buyer.dashboard.welcome')}</p>
        </div>

        {/* Main Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/furniture"
            className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
          >
            <div className="text-center">
              <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('buyer.dashboard.browse')}</h3>
              <p className="text-gray-600">{t('buyer.dashboard.browseDesc')}</p>
            </div>
          </Link>

          <Link
            to="/buyer/saved"
            className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
          >
            <div className="text-center">
              <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('buyer.dashboard.saved')}</h3>
              <p className="text-gray-600">{t('buyer.dashboard.savedDesc')}</p>
            </div>
          </Link>

          <Link
            to="/buyer/messages"
            className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
          >
            <div className="text-center">
              <div className="bg-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('navbar.messages')}</h3>
              <p className="text-gray-600">{t('buyer.dashboard.messagesDesc')}</p>
            </div>
          </Link>
        </div>

        {/* Favorite Categories */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('buyer.dashboard.browseByCategory')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {favoriteCategories.map((category) => (
              <Link
                key={category}
                to={`/furniture?category=${category}`}
                className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-center"
              >
                <div className="text-2xl mb-2">
                  {category === 'sofa' && '🛋️'}
                  {category === 'desk' && '🪑'}
                  {category === 'chair' && '💺'}
                  {category === 'bed' && '🛏️'}
                  {category === 'table' && '📋'}
                  {category === 'storage' && '🗄️'}
                </div>
                <p className="font-medium text-gray-900 capitalize">{t(`home.categories.${category}`)}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Items */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">{t('buyer.dashboard.recentlyAdded')}</h2>
            <Link to="/furniture" className="text-blue-600 hover:text-blue-800 font-medium">
              {t('home.viewAll')} →
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow animate-pulse">
                  <div className="h-48 bg-gray-300 rounded-t-lg"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentItems.map((item) => (
                <Link
                  key={item._id}
                  to={`/furniture/${item._id}`}
                  className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <img
                    src={item.images[0]}
                    alt={item.title}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{item.description.substring(0, 80)}...</p>
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-green-600">${item.price}</span>
                      <span className="text-sm text-gray-500 capitalize">{t(`furniture.condition.${item.condition}`)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyerDashboard;
